import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { cloneDeep, isEqual } from "lodash";
import { customFieldsMap, requiredFieldsPath } from "@investorRequest/constants";
import { hasValidationErrors } from "../utils/SpecificValidations";
import {
  CommonFields,
  CustomFields,
  InvestorRequestState,
  InvestorRequestType,
  DraftFormData,
  SessionAttachment,
  C2DSFieldErrors,
  C2DSFieldKey,
} from "../models";
import {
  sendInvestorRequestThunk,
  fetchCountriesThunk,
  getDraftValuesThunk,
  saveDraftThunk,
  deleteDraftThunk,
  submitWireInstructionThunk,
} from "./thunks";

// Helper function to detect if form has unsaved changes
const hasFormChanges = (
  currentCommonFields: CommonFields,
  currentCustomFields: CustomFields,
  lastSavedState: { commonFields: CommonFields; customFields: CustomFields } | null,
  requestType: InvestorRequestType | null
): boolean => {
  // If no saved state exists, compare with DEFAULTS (not just check for empty)
  if (!lastSavedState) {
    const hasNonEmptyFunds = currentCommonFields.selectedFunds.length > 0;
    const hasNonEmptyAccounts = currentCommonFields.selectedAccounts.length > 0;
    const hasNonEmptyMessage = currentCommonFields.message.trim().length > 0;
    const hasAttachment = currentCommonFields.attachment !== null;

    // Get default custom fields for this request type
    const defaultCustomFields = requestType ? customFieldsMap[requestType] : {};

    // Check if any custom field DIFFERS from its default value
    const hasNonDefaultCustomFields = Object.keys(currentCustomFields).some((key) => {
      // Skip paymentMethod and fundType as they are set programmatically
      if (key === "paymentMethod" || key === "fundType") return false;

      const currentValue = currentCustomFields[key];
      const defaultValue = defaultCustomFields[key];

      // Compare with default - if different, user has made changes
      return !isEqual(currentValue, defaultValue);
    });

    return hasNonEmptyFunds || hasNonEmptyAccounts || hasNonEmptyMessage || hasAttachment || hasNonDefaultCustomFields;
  }

  // If saved state exists, we need to compare ONLY the keys that exist in lastSavedState
  // Because current state might have additional default fields merged in

  // Compare commonFields (should be straightforward)
  const commonFieldsChanged = !isEqual(currentCommonFields, lastSavedState.commonFields);

  // For customFields, only compare keys that existed in lastSavedState
  // This prevents false positives from default fields that were merged later
  const savedCustomKeys = Object.keys(lastSavedState.customFields);
  let customFieldsChanged = false;

  for (const key of savedCustomKeys) {
    const currentValue = currentCustomFields[key];
    const savedValue = lastSavedState.customFields[key];

    if (!isEqual(currentValue, savedValue)) {
      customFieldsChanged = true;
      break;
    }
  }

  // Also check if current state has any NEW fields with non-empty values
  // (fields not in lastSavedState but added by user)
  const currentCustomKeys = Object.keys(currentCustomFields);

  for (const key of currentCustomKeys) {
    // Skip if it was in lastSavedState (already compared above)
    if (savedCustomKeys.includes(key)) continue;

    // Skip programmatic fields
    if (key === "paymentMethod" || key === "fundType") continue;

    const value = currentCustomFields[key];

    // Check if this new field has actual user data
    if (value !== undefined && value !== null && value !== "" && (typeof value !== "string" || value.trim() !== "")) {
      customFieldsChanged = true;
      break;
    }
  }

  return commonFieldsChanged || customFieldsChanged;
};

const initialCommonFields = {
  selectedFunds: [],
  selectedAccounts: [],
  message: "",
  attachment: null,
};

const initialState: InvestorRequestState = {
  open: false,
  requestType: null,
  commonFields: { ...initialCommonFields },
  customFields: {},
  // Tab Independence: Separate state for Internal and External tabs
  tabSpecificData: {
    internal: {
      commonFields: { ...initialCommonFields },
      customFields: {},
      hasBeenInitialized: false,
      // LPC-9775: Draft attachment metadata per tab
      draftAttachmentId: null,
      draftAttachmentName: null,
      draftAttachmentURL: null,
      // Track unsaved changes for "Save as Draft" button state
      hasUnsavedChanges: false,
      lastSavedState: null,
    },
    external: {
      commonFields: { ...initialCommonFields },
      customFields: {},
      hasBeenInitialized: false,
      // LPC-9775: Draft attachment metadata per tab
      draftAttachmentId: null,
      draftAttachmentName: null,
      draftAttachmentURL: null,
      // Track unsaved changes for "Save as Draft" button state
      hasUnsavedChanges: false,
      lastSavedState: null,
    },
  },
  retainedCustomFields: cloneDeep(customFieldsMap),
  loading: false,
  validationWarningShown: false,
  wireUnavailable: false,
  activeInfoTab: "Internal Fund Information",
  countries: {
    loading: false,
    data: [],
  },
  submitSuccess: false,
  draft: {
    loading: false,
    attachmentId: null,
    attachmentName: null,
    attachmentURL: null,
  },
  // LPC-9775: Session attachments for hybrid users (cleared on dialog close or submit)
  sessionAttachments: {
    internal: null,
    external: null,
  },
  // LPC-10289: C2DS validation errors mapped to form fields
  c2dsErrors: null,
};

const investorRequestSlice = createSlice({
  name: "investorRequest",
  initialState,
  reducers: {
    openInvestorRequestDialog: (
      state,
      action: PayloadAction<{ requestType: InvestorRequestType; customFields?: CustomFields } | undefined>
    ) => {
      state.open = true;
      state.submitSuccess = false;

      if (!action.payload) return;

      state.requestType = action.payload.requestType;

      // Get default values for the request type
      const defaultCustomFields = customFieldsMap[action.payload.requestType];
      const defaultCommonFields = {
        selectedFunds: [],
        selectedAccounts: [],
        message: "",
        attachment: null,
      };

      // Bug Fix: Reset global commonFields and draft to prevent old data from showing
      state.commonFields = { ...defaultCommonFields };
      state.customFields = { ...defaultCustomFields };
      state.draft = {
        loading: false,
        attachmentId: null,
        attachmentName: null,
        attachmentURL: null,
      };

      // Bug Fix: Reset both tabs to allow draft loading on first visit
      // This ensures that when dialog opens, both Internal and External tabs can load their drafts
      state.tabSpecificData.internal.customFields = { ...defaultCustomFields };
      state.tabSpecificData.internal.commonFields = { ...defaultCommonFields };
      state.tabSpecificData.internal.hasBeenInitialized = false;
      // LPC-9775: Reset draft attachment metadata
      state.tabSpecificData.internal.draftAttachmentId = null;
      state.tabSpecificData.internal.draftAttachmentName = null;
      state.tabSpecificData.internal.draftAttachmentURL = null;
      // Reset change tracking
      state.tabSpecificData.internal.hasUnsavedChanges = false;
      state.tabSpecificData.internal.lastSavedState = null;

      state.tabSpecificData.external.customFields = { ...defaultCustomFields };
      state.tabSpecificData.external.commonFields = { ...defaultCommonFields };
      state.tabSpecificData.external.hasBeenInitialized = false;
      // LPC-9775: Reset draft attachment metadata
      state.tabSpecificData.external.draftAttachmentId = null;
      state.tabSpecificData.external.draftAttachmentName = null;
      state.tabSpecificData.external.draftAttachmentURL = null;
      // Reset change tracking
      state.tabSpecificData.external.hasUnsavedChanges = false;
      state.tabSpecificData.external.lastSavedState = null;

      if (action.payload.customFields) {
        state.customFields = action.payload.customFields;

        state.commonFields.selectedAccounts = action.payload.customFields.filterParamsInvestorAccounts;
        state.commonFields.selectedFunds = action.payload.customFields.filterParamsFunds;

        // Tab Independence: Initialize current tab with provided data
        const currentTab = state.activeInfoTab === "Internal Fund Information" ? "internal" : "external";

        state.tabSpecificData[currentTab].customFields = { ...state.customFields };
        state.tabSpecificData[currentTab].commonFields = { ...state.commonFields };
      } else {
        state.customFields = defaultCustomFields;

        // Tab Independence: Set current tab's data
        const currentTab = state.activeInfoTab === "Internal Fund Information" ? "internal" : "external";

        state.tabSpecificData[currentTab].customFields = { ...defaultCustomFields };
        state.tabSpecificData[currentTab].commonFields = { ...state.commonFields };
      }
    },
    closeInvestorRequestDialog: (state) => {
      state.open = false;
      // LPC-9775: Clear session attachments when dialog closes
      state.sessionAttachments.internal = null;
      state.sessionAttachments.external = null;
      // LPC-10289: Clear C2DS errors when dialog closes
      state.c2dsErrors = null;

      // Clear global draft state
      state.draft = {
        loading: false,
        attachmentId: null,
        attachmentName: null,
        attachmentURL: null,
      };

      // Tab Independence: Clear both tabs' data when dialog closes
      const emptyCommonFields = {
        selectedFunds: [],
        selectedAccounts: [],
        message: "",
        attachment: null,
      };

      // Bug Fix: Initialize with default values instead of empty objects
      const defaultCustomFields = state.requestType ? customFieldsMap[state.requestType] : {};

      state.tabSpecificData.internal.commonFields = { ...emptyCommonFields };
      state.tabSpecificData.internal.customFields = { ...defaultCustomFields };
      state.tabSpecificData.internal.hasBeenInitialized = false;
      state.tabSpecificData.internal.hasUnsavedChanges = false;
      state.tabSpecificData.internal.lastSavedState = null;
      state.tabSpecificData.internal.draftAttachmentId = null;
      state.tabSpecificData.internal.draftAttachmentName = null;
      state.tabSpecificData.internal.draftAttachmentURL = null;

      state.tabSpecificData.external.commonFields = { ...emptyCommonFields };
      state.tabSpecificData.external.customFields = { ...defaultCustomFields };
      state.tabSpecificData.external.hasBeenInitialized = false;
      state.tabSpecificData.external.hasUnsavedChanges = false;
      state.tabSpecificData.external.lastSavedState = null;
      state.tabSpecificData.external.draftAttachmentId = null;
      state.tabSpecificData.external.draftAttachmentName = null;
      state.tabSpecificData.external.draftAttachmentURL = null;
    },
    selectRequestType: (state, action: PayloadAction<{ requestType: InvestorRequestType }>) => {
      if (state.requestType) {
        state.retainedCustomFields[state.requestType] = {
          ...state.customFields,
        };
      }

      state.validationWarningShown = false;
      const previousRequestType = state.requestType;

      state.requestType = action.payload.requestType;
      state.customFields = state.retainedCustomFields[action.payload.requestType];

      if (action.payload.requestType !== InvestorRequestType.UpdateWireInstructions) {
        state.wireUnavailable = false;
      }

      // Bug Fix: Clear selected funds, accounts, and attachment when switching AWAY FROM Update Wire Instructions
      // This ensures dropdowns and attachments are reset when changing to other request types
      // We do NOT clear when switching TO Update Wire Instructions (to allow drafts to load)
      if (
        previousRequestType === InvestorRequestType.UpdateWireInstructions &&
        action.payload.requestType !== InvestorRequestType.UpdateWireInstructions
      ) {
        state.commonFields.selectedFunds = [];
        state.commonFields.selectedAccounts = [];
        state.commonFields.attachment = null;
      }

      // Bug Fix: Reset hasBeenInitialized flags when switching TO Update Wire Instructions
      // This triggers the draft loading useEffect in WireInstructionsRequest
      if (
        previousRequestType !== InvestorRequestType.UpdateWireInstructions &&
        action.payload.requestType === InvestorRequestType.UpdateWireInstructions
      ) {
        state.tabSpecificData.internal.hasBeenInitialized = false;
        state.tabSpecificData.external.hasBeenInitialized = false;
      }
    },
    setActiveInfoTab: (state, action: PayloadAction<string>) => {
      // Tab Independence: Save current tab data before switching
      const currentTab = state.activeInfoTab === "Internal Fund Information" ? "internal" : "external";
      const targetTab = action.payload === "Internal Fund Information" ? "internal" : "external";

      // Save current tab's data to tab-specific storage and mark as initialized
      state.tabSpecificData[currentTab].commonFields = { ...state.commonFields };
      state.tabSpecificData[currentTab].customFields = { ...state.customFields };
      state.tabSpecificData[currentTab].hasBeenInitialized = true;

      // LPC-9775: Save current tab's draft attachment metadata
      state.tabSpecificData[currentTab].draftAttachmentId = state.draft.attachmentId;
      state.tabSpecificData[currentTab].draftAttachmentName = state.draft.attachmentName;
      state.tabSpecificData[currentTab].draftAttachmentURL = state.draft.attachmentURL;

      // Check if current tab has unsaved changes before switching
      // ONLY if the current tab has a saved baseline to compare against
      // If lastSavedState is null, there's no baseline, so we can't detect changes accurately
      if (state.tabSpecificData[currentTab].lastSavedState !== null) {
        state.tabSpecificData[currentTab].hasUnsavedChanges = hasFormChanges(
          state.commonFields,
          state.customFields,
          state.tabSpecificData[currentTab].lastSavedState,
          state.requestType
        );
      }

      // Switch to target tab
      state.activeInfoTab = action.payload;

      // Load target tab's data from tab-specific storage
      // Bug Fix: Merge with default values to ensure fields like intermediaryYesNo and furtherCreditYesNo
      // have their default "no" values when opening a tab for the first time
      const defaultCustomFields = state.requestType ? customFieldsMap[state.requestType] : {};

      state.commonFields = { ...state.tabSpecificData[targetTab].commonFields };
      state.customFields = {
        ...defaultCustomFields,
        ...state.tabSpecificData[targetTab].customFields,
      };

      // LPC-9775: Restore target tab's draft attachment metadata
      state.draft.attachmentId = state.tabSpecificData[targetTab].draftAttachmentId;
      state.draft.attachmentName = state.tabSpecificData[targetTab].draftAttachmentName;
      state.draft.attachmentURL = state.tabSpecificData[targetTab].draftAttachmentURL;
    },
    updateCommonRequestData: (state, action: PayloadAction<Partial<CommonFields>>) => {
      state.commonFields = {
        ...state.commonFields,
        ...action.payload,
      };

      // Tab Independence: Update current tab's data
      const currentTab = state.activeInfoTab === "Internal Fund Information" ? "internal" : "external";

      state.tabSpecificData[currentTab].commonFields = { ...state.commonFields };

      // Bug Fix: Only mark as initialized if user is ACTUALLY editing, not just programmatic clearing
      // When switching tabs, we clear selections with {selectedFunds: [], selectedAccounts: []}
      // These should NOT mark the tab as initialized
      // BUT: attachment changes should ALWAYS trigger change detection
      const isProgrammaticClear =
        action.payload.selectedFunds?.length === 0 &&
        action.payload.selectedAccounts?.length === 0 &&
        Object.keys(action.payload).length === 2 &&
        !action.payload.message &&
        !action.payload.attachment;

      if (!isProgrammaticClear) {
        state.tabSpecificData[currentTab].hasBeenInitialized = true;
      }

      // ALWAYS check for unsaved changes (even for attachment removal)
      // This ensures button state is correct when user removes attachment
      state.tabSpecificData[currentTab].hasUnsavedChanges = hasFormChanges(
        state.commonFields,
        state.customFields,
        state.tabSpecificData[currentTab].lastSavedState,
        state.requestType
      );

      if (state.validationWarningShown) {
        const baseRequired = requiredFieldsPath[state.requestType!];

        state.validationWarningShown = hasValidationErrors(state as any, baseRequired);
      }
    },
    updateCustomRequestData: (state, action: PayloadAction<{ [key: string]: string }>) => {
      state.customFields = {
        ...state.customFields,
        ...action.payload,
      };

      // Tab Independence: Update current tab's data
      const currentTab = state.activeInfoTab === "Internal Fund Information" ? "internal" : "external";

      state.tabSpecificData[currentTab].customFields = { ...state.customFields };

      // Bug Fix: Only mark as initialized if user is ACTUALLY editing, not programmatic defaults
      // Setting default payment method or fundType should NOT mark as initialized (prevents draft loading)
      const isProgrammaticDefault =
        (Object.keys(action.payload).length === 1 && action.payload.paymentMethod) || // Only setting payment method
        (Object.keys(action.payload).length === 1 && action.payload.fundType) || // Only setting fundType
        (Object.keys(action.payload).length === 2 && action.payload.paymentMethod && action.payload.fundType); // Setting both

      if (!isProgrammaticDefault) {
        state.tabSpecificData[currentTab].hasBeenInitialized = true;

        // Check if form has unsaved changes
        state.tabSpecificData[currentTab].hasUnsavedChanges = hasFormChanges(
          state.commonFields,
          state.customFields,
          state.tabSpecificData[currentTab].lastSavedState,
          state.requestType
        );
      }

      // Reset validation warnings when payment method changes to avoid showing stale errors
      if (Object.prototype.hasOwnProperty.call(action.payload, "paymentMethod")) {
        state.validationWarningShown = false;
        // LPC-10289: Clear C2DS errors when payment method changes
        state.c2dsErrors = null;
      }

      if (state.validationWarningShown) {
        const baseRequired = requiredFieldsPath[state.requestType!];

        state.validationWarningShown = hasValidationErrors(state as any, baseRequired);
      }
    },
    validateFields: (state) => {
      const baseRequired = requiredFieldsPath[state.requestType!];

      state.validationWarningShown = hasValidationErrors(state as any, baseRequired);
    },
    setWireUnavailable: (state, action: PayloadAction<boolean>) => {
      state.wireUnavailable = action.payload;
    },
    resetInvestorRequestState: (state) => {
      state.requestType = initialState.requestType;
      state.commonFields = { ...initialState.commonFields };
      state.customFields = initialState.customFields;
      state.validationWarningShown = initialState.validationWarningShown;
      state.retainedCustomFields = cloneDeep(customFieldsMap);
      state.submitSuccess = initialState.submitSuccess;
      state.activeInfoTab = initialState.activeInfoTab;
      state.draft = initialState.draft;
      // LPC-9775: Clear session attachments on reset
      state.sessionAttachments = initialState.sessionAttachments;

      // Tab Independence: Reset both tabs' data
      const emptyCommonFields = {
        selectedFunds: [],
        selectedAccounts: [],
        message: "",
        attachment: null,
      };

      // Bug Fix: Initialize with default values instead of empty objects
      const defaultCustomFields = state.requestType ? customFieldsMap[state.requestType] : {};

      state.tabSpecificData.internal.commonFields = { ...emptyCommonFields };
      state.tabSpecificData.internal.customFields = { ...defaultCustomFields };
      state.tabSpecificData.internal.hasBeenInitialized = false;
      state.tabSpecificData.internal.hasUnsavedChanges = false;
      state.tabSpecificData.internal.lastSavedState = null;

      state.tabSpecificData.external.commonFields = { ...emptyCommonFields };
      state.tabSpecificData.external.customFields = { ...defaultCustomFields };
      state.tabSpecificData.external.hasBeenInitialized = false;
      state.tabSpecificData.external.hasUnsavedChanges = false;
      state.tabSpecificData.external.lastSavedState = null;
    },
    // LPC-9775: Load draft data into form
    loadDraftData: (state, action: PayloadAction<DraftFormData & { isActualDraft?: boolean }>) => {
      // Replace commonFields completely (except attachment which needs special handling)
      if (action.payload.commonFields) {
        state.commonFields = {
          selectedFunds: action.payload.commonFields.selectedFunds || [],
          selectedAccounts: action.payload.commonFields.selectedAccounts || [],
          message: action.payload.commonFields.message || "",
          attachment:
            action.payload.commonFields.attachment !== undefined && action.payload.commonFields.attachment !== null
              ? action.payload.commonFields.attachment
              : state.commonFields.attachment,
        };
      }

      // Replace customFields completely
      if (action.payload.customFields) {
        state.customFields = {
          ...action.payload.customFields,
        };
      }

      // Tab Independence: Update current tab's data in tabSpecificData and mark as initialized
      const currentTab = state.activeInfoTab === "Internal Fund Information" ? "internal" : "external";

      state.tabSpecificData[currentTab].commonFields = { ...state.commonFields };
      state.tabSpecificData[currentTab].customFields = { ...state.customFields };
      state.tabSpecificData[currentTab].hasBeenInitialized = true;

      // Save this as the baseline for change detection
      // BUT: Only if this is an actual draft load, not just initializing with defaults
      // When loading defaults (no draft exists), we don't want to set a baseline yet
      // because the default payment method will be set after this, causing false positive
      const isActualDraft = action.payload.isActualDraft !== false; // Default to true for backward compatibility

      if (isActualDraft) {
        state.tabSpecificData[currentTab].lastSavedState = {
          commonFields: { ...state.commonFields },
          customFields: { ...state.customFields },
        };
      }
      // Always reset hasUnsavedChanges after loading draft data (whether actual draft or clearing)
      // This ensures button is disabled after:
      // 1. Loading a saved draft
      // 2. Clearing all fields (which loads empty defaults)
      state.tabSpecificData[currentTab].hasUnsavedChanges = false;
    },
    // LPC-9775: Clear draft attachment ID
    clearDraftAttachmentId: (state) => {
      state.draft.attachmentId = null;
      state.draft.attachmentName = null;
      state.draft.attachmentURL = null;

      // Also clear current tab's draft attachment metadata
      const currentTab = state.activeInfoTab === "Internal Fund Information" ? "internal" : "external";

      state.tabSpecificData[currentTab].draftAttachmentId = null;
      state.tabSpecificData[currentTab].draftAttachmentName = null;
      state.tabSpecificData[currentTab].draftAttachmentURL = null;
    },
    // LPC-9775: Set draft attachment info
    setDraftAttachmentInfo: (
      state,
      action: PayloadAction<{ attachmentId: string; attachmentName: string; attachmentURL?: string }>
    ) => {
      state.draft.attachmentId = action.payload.attachmentId;
      state.draft.attachmentName = action.payload.attachmentName;
      state.draft.attachmentURL = action.payload.attachmentURL || null;

      // Also update current tab's draft attachment metadata
      const currentTab = state.activeInfoTab === "Internal Fund Information" ? "internal" : "external";

      state.tabSpecificData[currentTab].draftAttachmentId = action.payload.attachmentId;
      state.tabSpecificData[currentTab].draftAttachmentName = action.payload.attachmentName;
      state.tabSpecificData[currentTab].draftAttachmentURL = action.payload.attachmentURL || null;
    },
    // Reset form fields after successful submission (keeps requestType)
    // LPC-9775: Takes optional fundType to clear only that session (not both)
    resetFormFields: (state, action: PayloadAction<{ fundType?: string } | undefined>) => {
      const emptyCommonFields = {
        selectedFunds: [],
        selectedAccounts: [],
        message: "",
        attachment: null,
      };

      state.commonFields = { ...emptyCommonFields };
      state.customFields = customFieldsMap[state.requestType!] || {};
      state.validationWarningShown = false;
      state.draft.attachmentId = null;
      state.draft.attachmentName = null;
      state.draft.attachmentURL = null;

      // Tab Independence: Clear only the current tab's data and reset initialized flag
      const currentTab = state.activeInfoTab === "Internal Fund Information" ? "internal" : "external";

      state.tabSpecificData[currentTab].commonFields = { ...emptyCommonFields };
      state.tabSpecificData[currentTab].customFields = { ...state.customFields };
      state.tabSpecificData[currentTab].hasBeenInitialized = false;
      // Reset change tracking after submit
      state.tabSpecificData[currentTab].hasUnsavedChanges = false;
      state.tabSpecificData[currentTab].lastSavedState = null;

      // LPC-9775: Clear session attachment for submitted fundType only (preserve other tab)
      if (action.payload?.fundType) {
        const key = action.payload.fundType.toLowerCase() as "internal" | "external";

        state.sessionAttachments[key] = null;
      } else {
        // Fallback: clear both if fundType not provided (backward compatibility)
        state.sessionAttachments.internal = null;
        state.sessionAttachments.external = null;
      }
    },
    // LPC-9775: Save attachment to session for specific fund type (Internal/External)
    saveAttachmentToSession: (
      state,
      action: PayloadAction<{
        fundType: "Internal" | "External";
        attachment: SessionAttachment;
      }>
    ) => {
      const key = action.payload.fundType.toLowerCase() as "internal" | "external";

      state.sessionAttachments[key] = action.payload.attachment;
    },
    // LPC-9775: Clear session attachment for specific fund type
    clearSessionAttachment: (state, action: PayloadAction<"Internal" | "External">) => {
      const key = action.payload.toLowerCase() as "internal" | "external";

      state.sessionAttachments[key] = null;
    },
    // LPC-9775: Clear all session attachments
    clearAllSessionAttachments: (state) => {
      state.sessionAttachments.internal = null;
      state.sessionAttachments.external = null;
    },
    // LPC-9775: Clear submit success flag (used after handling Submit Another Form dialog)
    clearSubmitSuccess: (state) => {
      state.submitSuccess = false;
    },
    // LPC-10289: Set C2DS validation errors
    setC2DSErrors: (state, action: PayloadAction<C2DSFieldErrors | null>) => {
      state.c2dsErrors = action.payload;
    },
    // LPC-10289: Clear a specific C2DS field error (called when user modifies the field)
    clearC2DSFieldError: (state, action: PayloadAction<C2DSFieldKey>) => {
      if (state.c2dsErrors) {
        state.c2dsErrors[action.payload] = null;
        // Check if all errors are cleared
        const hasAnyError = Object.values(state.c2dsErrors).some((error) => error !== null);

        if (!hasAnyError) {
          state.c2dsErrors = null;
        }
      }
    },
    // LPC-10289: Clear all C2DS errors
    clearAllC2DSErrors: (state) => {
      state.c2dsErrors = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendInvestorRequestThunk.pending, (state) => {
        state.loading = true;
        state.submitSuccess = false;
      })
      .addCase(sendInvestorRequestThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.submitSuccess = !!action.payload;
      })
      .addCase(sendInvestorRequestThunk.rejected, (state) => {
        state.loading = false;
        state.submitSuccess = false;
      })
      .addCase(fetchCountriesThunk.pending, (state) => {
        state.countries.loading = true;
      })
      .addCase(fetchCountriesThunk.fulfilled, (state, action) => {
        state.countries.loading = false;
        state.countries.data = action.payload;
      })
      .addCase(fetchCountriesThunk.rejected, (state) => {
        state.countries.loading = false;
      })
      // LPC-9775: Draft thunks
      .addCase(getDraftValuesThunk.pending, (state) => {
        state.draft.loading = true;
      })
      .addCase(getDraftValuesThunk.fulfilled, (state, action) => {
        state.draft.loading = false;

        // LPC-9775: Process attachment data from draft response
        // This ensures download functionality works immediately after saving draft
        if (action.payload) {
          try {
            const instructionsJson = action.payload.wireInstructions?.instructions;

            if (instructionsJson) {
              const parsedData: DraftFormData = JSON.parse(instructionsJson);

              // Update draft attachment metadata if present in the response
              if (parsedData.attachmentId && parsedData.attachmentName) {
                // Use the fundType passed to the thunk (action.meta.arg) as the source of truth
                // The API fetches draft based on fundType parameter, so we don't need to rely on JSON
                const requestedFundType = action.meta.arg; // "Internal" or "External"
                const tabKey = requestedFundType.toLowerCase() as "internal" | "external";

                // Update tab-specific draft metadata
                state.tabSpecificData[tabKey].draftAttachmentId = parsedData.attachmentId;
                state.tabSpecificData[tabKey].draftAttachmentName = parsedData.attachmentName;
                state.tabSpecificData[tabKey].draftAttachmentURL = parsedData.AttachmentURL || null;

                // Update global draft state (always update since we fetched for this specific fundType)
                state.draft.attachmentId = parsedData.attachmentId;
                state.draft.attachmentName = parsedData.attachmentName;
                state.draft.attachmentURL = parsedData.AttachmentURL || null;

                // Update commonFields.attachment to display the chip
                // Only if:
                // 1. There's no session attachment (session takes priority)
                // 2. The request type is Update Wire Instructions (prevents showing attachment before user selects it)
                if (
                  !state.sessionAttachments[tabKey] &&
                  state.requestType === InvestorRequestType.UpdateWireInstructions
                ) {
                  state.commonFields.attachment = {
                    name: parsedData.attachmentName,
                    type: "application/pdf" as any,
                    base64: "", // Empty since we don't have the actual file
                  };
                }
              }
            }
          } catch (error) {
            // Silently handle parse errors
          }
        }
      })
      .addCase(getDraftValuesThunk.rejected, (state) => {
        state.draft.loading = false;
      })
      .addCase(saveDraftThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(saveDraftThunk.fulfilled, (state, action) => {
        state.loading = false;
        // LPC-9775: Clear session attachment for the saved fundType
        // Draft is now the source of truth for this fundType
        const fundType = action.meta.arg.fundType;
        const key = fundType.toLowerCase() as "internal" | "external";

        state.sessionAttachments[key] = null;

        // Update lastSavedState to current state and mark as no unsaved changes
        state.tabSpecificData[key].lastSavedState = {
          commonFields: { ...state.commonFields },
          customFields: { ...state.customFields },
        };
        state.tabSpecificData[key].hasUnsavedChanges = false;
      })
      .addCase(saveDraftThunk.rejected, (state) => {
        state.loading = false;
      })
      .addCase(deleteDraftThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteDraftThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.draft.attachmentId = null;
        state.draft.attachmentName = null;
        state.draft.attachmentURL = null;
        // LPC-9775: Clear session attachment for the deleted fundType
        const fundType = action.meta.arg.fundType;
        const key = fundType.toLowerCase() as "internal" | "external";

        state.sessionAttachments[key] = null;

        // Also clear tab-specific draft attachment metadata for the deleted fundType
        state.tabSpecificData[key].draftAttachmentId = null;
        state.tabSpecificData[key].draftAttachmentName = null;
        state.tabSpecificData[key].draftAttachmentURL = null;

        // Clear change tracking - no saved state to compare against
        state.tabSpecificData[key].hasUnsavedChanges = false;
        state.tabSpecificData[key].lastSavedState = null;
      })
      .addCase(deleteDraftThunk.rejected, (state) => {
        state.loading = false;
      })
      .addCase(submitWireInstructionThunk.pending, (state) => {
        state.loading = true;
        state.submitSuccess = false;
      })
      .addCase(submitWireInstructionThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.submitSuccess = action.payload.success;
      })
      .addCase(submitWireInstructionThunk.rejected, (state) => {
        state.loading = false;
        state.submitSuccess = false;
      });
  },
});

export const {
  openInvestorRequestDialog,
  closeInvestorRequestDialog,
  selectRequestType,
  updateCommonRequestData,
  updateCustomRequestData,
  validateFields,
  resetInvestorRequestState,
  setWireUnavailable,
  setActiveInfoTab,
  loadDraftData,
  clearDraftAttachmentId,
  setDraftAttachmentInfo,
  resetFormFields,
  saveAttachmentToSession,
  clearSessionAttachment,
  clearAllSessionAttachments,
  clearSubmitSuccess,
  // LPC-10289: C2DS error actions
  setC2DSErrors,
  clearC2DSFieldError,
  clearAllC2DSErrors,
} = investorRequestSlice.actions;
export default investorRequestSlice.reducer;
